import React, { useEffect, useState } from "react";
import Layout from "../layouts/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { getEnterpriseByUuid } from "../services/api/bidderService";
import { toast } from "react-toastify";
import { Button, Card, CardContent, CardHeader, Chip, Divider, Grid, Paper, Tooltip, Typography } from "@mui/material";
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneIcon from '@mui/icons-material/Phone';
import SentimentIconRating from "../components/ui/SentimentIconRating";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { OfferStatus } from "../helpers/bidderHelper";
import { DataGrid } from "@mui/x-data-grid";
import { prettyIdProcurement } from "../helpers/formatHelper";
import StatusBadge from "../components/ui/StatusBadge";
import { getColorByProcurementType, getLabelByProcurementType } from "../helpers/procurementHelper";
import { frFR } from "@mui/x-data-grid/locales";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export default function Enterprise() {
  const { uuidEnterprise } = useParams();
  const { isLoading, setIsLoading } = useLoading();
  const navigate = useNavigate();

  const [enterprise, setEnterprise] = useState();
  const [contacts, setContacts] = useState([]);
  const [procurements, setProcurements] = useState([]);
  const [avgRating, setAvgRating] = useState();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    async function fetch() {
      try {
        setIsLoading(true);
        const result = await getEnterpriseByUuid(uuidEnterprise);
        if (result) {
          setEnterprise(result);

          const avgRating = result.reviews.length > 0 ? Math.round(result.reviews.reduce((a, b) => a + b.rating.ratingValue, 0) / result.reviews.length) : 5;
          setAvgRating(avgRating);

          const contacts = Object.values(result.contacts.reduce((acc, contact) => {
            const key = contact.fullname;
            if (!acc[key]) {
              acc[key] = {
                uuid: contact.uuid,
                fullname: contact.fullname,
                emails: [],
                mobile: contact.mobile,
                civility: contact.civility,
                job: contact.job,
              };
            }
            acc[key].emails.push(contact.email);
            return acc;
          }, {}));
          setContacts(contacts);

          const procurements = result.offers.filter(offer => offer.status === OfferStatus.SELECTED).map(offer => offer.procurement);
          setProcurements(procurements);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, [setIsLoading, uuidEnterprise]);

  function handleClick(event, uuid) {
    const url = `/procurement/${uuid}`;
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  }

  if (isLoading || !enterprise) {
    return <Layout />;
  }

  return (
    <Layout>
      <div className="flex flex-col pt-16 px-5 py-4 lg:pt-4 lg:mx-72 h-full overflow-y-auto gap-6">

        <div className="flex justify-between">
          <h1 className="text-2xl mb-2 lg:text-4xl font-semibold">
            {enterprise.name}
          </h1>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => navigate("/enterprises")}
            startIcon={<ArrowBackIosNewIcon />}
          >
            Retour
          </Button>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Informations</h2>
          <Card className="max-w-lg">
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <Typography variant="body1" className="text-gray-600">
                  Numéro d'entreprise :
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {enterprise.enterpriseNumber}
                </Typography>
              </div>

              {enterprise.legalForm && (
                <div className="flex justify-between">
                  <Typography variant="body1" className="text-gray-600">
                    Forme légale :
                  </Typography>
                  <Typography variant="body1" className="font-semibold max-w-64">
                    {enterprise.legalForm}
                  </Typography>
                </div>
              )}

              {enterprise.rawStreet && enterprise.postalCode && enterprise.locality && enterprise.country && (
                <div className="flex justify-between">
                  <Typography variant="body1" className="text-gray-600">
                    Adresse :
                  </Typography>
                  <div className="flex flex-col items-end max-w-64">
                    <Typography variant="body1" className="font-semibold">
                      {enterprise.rawStreet}
                    </Typography>
                    <Typography variant="body1" className="font-semibold">
                      {enterprise.postalCode} {enterprise.locality}
                    </Typography>
                    <Typography variant="body1" className="font-semibold">
                      {enterprise.country}
                    </Typography>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold">Liste des contacts</h2>
          {contacts.length === 0
            ? (
              <p className="text-gray-500 italic">Aucun contact pour cette entreprise.</p>
            )
            : (
              <Grid container spacing={2} columns={{ xs: 1, lg: 2, xl: 3 }}>
                {contacts.map(contact => (
                  <Grid key={contact.uuid} size={1}>
                    <Card>
                      <CardHeader
                        title={contact.fullname}
                        subheader={contact.job}
                        sx={{ backgroundColor: "lightblue" }}
                      />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={2} columns={2}>
                          <Grid size={1}>
                            <div className="flex gap-2">
                              <AlternateEmailIcon />
                              <span className="font-semibold">Email{contact.emails.length > 1 && "s"}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {contact.emails.map((email, idx) => (
                                <Chip key={`email-${idx}`} component="a" label={email} href={`mailto:${email}`} clickable />
                              ))}
                            </div>
                          </Grid>
                          <Grid size={1}>
                            <div className="flex gap-2">
                              <PhoneIcon />
                              <span className="font-semibold">Téléphone</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {contact.mobile
                                ? <Chip component="a" label={contact.mobile} href={`tel:${contact.mobile}`} clickable />
                                : "—"}
                            </div>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold">Marchés remportés</h2>
          {procurements.length === 0
            ? (
              <p className="text-gray-500 italic">Aucun marché remporté par cette entreprise.</p>
            )
            : (
              <Paper className="max-w-6xl">
                <DataGrid
                  rows={procurements}
                  columns={[
                    {
                      field: "id",
                      headerName: "Numéro",
                      flex: 1,
                      renderCell: params => <span>{prettyIdProcurement(params.value)}</span>,
                    },
                    {
                      field: "name",
                      headerName: "Nom du marché",
                      flex: 2,
                      renderCell: params => (
                        <span
                          className="flex w-full text-sky-500 cursor-pointer"
                          onMouseDown={event => handleClick(event, params.row.uuid)}
                        >
                          <span className="underline decoration-sky-500">{params.value}</span>
                        </span>
                      ),
                    },
                    {
                      field: "type",
                      headerName: "Type",
                      flex: 1,
                      renderCell: params => <StatusBadge text={getLabelByProcurementType(params.row.type)} color={getColorByProcurementType(params.row.type)} size="small" />,
                    },
                  ]}
                  paginationModel={paginationModel}
                  pageSizeOptions={[10, 25, 50, 100]}
                  onPaginationModelChange={model => setPaginationModel(model)}
                  localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                />
              </Paper>
            )}
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold">
            <span>Notes des collègues </span>
            <Tooltip
              title="Appréciation générale de l'entreprise"
            >
              <SentimentIconRating rating={avgRating} />
            </Tooltip>
          </h2>
          {enterprise.reviews.length === 0
            ? (
              <p className="text-gray-500 italic">Aucun avis pour cette entreprise.</p>
            )
            : (
              <Grid container spacing={2} columns={{ xs: 1, lg: 2, xl: 3 }}>
                {enterprise.reviews.map(review => (
                  <Grid key={review.uuid} size={1} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardHeader
                        title={review.author.fullname}
                        subheader={`Publiée le ${format(review.publishedAt, 'dd/MM/yyyy')}`}
                        action={(
                          <SentimentIconRating rating={review.rating.ratingValue} />
                        )}
                      />
                      <CardContent>
                        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.reviewBody) }} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
        </div>

      </div>
    </Layout>
  );
}
